import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Ultra-Gentle Cream Cleanser",
    description: "A fragrance-free, non-foaming cleanser that won't irritate reactive skin.",
    bestFor: "Sensitive & Reactive Skin",
    benefits: ["Fragrance-free", "No foaming agents", "Maintains moisture barrier"]
  },
  {
    name: "Ceramide Moisturizer",
    description: "Rich in skin-identical lipids that repair and strengthen the barrier.",
    bestFor: "Sensitive & Dry Skin",
    benefits: ["Barrier repair", "Long-lasting hydration", "Dermatologist-recommended"]
  },
  {
    name: "Centella Asiatica Serum",
    description: "A soothing serum that calms redness and supports skin healing.",
    bestFor: "Redness & Irritation",
    benefits: ["Calms inflammation", "Promotes healing", "Gentle for daily use"]
  },
  {
    name: "Mineral Sunscreen SPF 50",
    description: "Physical (zinc) sunscreen that's gentle on sensitive skin.",
    bestFor: "Sensitive Skin",
    benefits: ["Physical/mineral only", "No chemical filters", "Gentle formula"]
  },
  {
    name: "Azelaic Acid (10%)",
    description: "A gentle multi-tasker for redness, texture, and uneven tone.",
    bestFor: "Rosacea-Prone Skin",
    benefits: ["Reduces redness", "Gentle on skin", "Evens tone"]
  },
  {
    name: "Thermal Spring Water Mist",
    description: "A soothing mist to calm and hydrate throughout the day.",
    bestFor: "All Sensitive Skin",
    benefits: ["Instant soothing", "Mineral-rich", "On-the-go relief"]
  }
];

const SensitiveSkin = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="sensitive-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Skin Type
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Sensitive Skin Guide
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            When your skin reacts to seemingly everything, finding safe products can feel impossible. This guide focuses on gentle, barrier-supporting options for reactive skin.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Understanding Sensitive Skin
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Sensitive skin can be triggered by many factors—fragrances, certain ingredients, weather changes, or a compromised skin barrier. The key is to keep your routine minimal and focused on barrier repair.
          </p>
          <p className="text-[#374151] leading-relaxed mb-4">
            For those with melanin-rich skin, sensitivity is especially important to manage: irritation can trigger post-inflammatory hyperpigmentation, leaving behind dark marks that take months to fade.
          </p>
          <p className="text-[#374151] leading-relaxed">
            Less is more with sensitive skin. A simple routine of cleanser, moisturizer, and sunscreen is often all you need.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="sensitive-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Recommended Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Avoid fragrance:</strong> Both synthetic and natural fragrances can trigger reactions.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Patch test everything:</strong> Apply a small amount to your inner arm before using on your face.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Introduce slowly:</strong> Add only one new product at a time, waiting 2 weeks between additions.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Look for "for sensitive skin":</strong> These products are typically tested on reactive skin types.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consider your triggers:</strong> Keep a log of products that cause reactions to identify patterns.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>
    </Layout>
  );
};

export default SensitiveSkin;
